import { ApolloClient, from, InMemoryCache } from "@apollo/client";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import {
    GetCompanyGlobalDocument,
    GetUserForGlobalVarDocument,
    MeUserDocument,
} from "../../main/globalVar/graphql/globalVar.hooks";
import {
    currencySingVar,
    currentBranchIdVar,
    currentCompanyIdVar,
    currentUserEmailVar,
    userCompaniesCountVar,
    userIdVar,
    userUsernameVar,
    companyBusinessTypeVar,
} from "../../main/globalVar/state";
import { UserBranchRelationProxy, CompanyProxy, BranchProxy } from "../../main/graphql/types";
import AccessService from "../../services/AccessService";
import { matchRoutes } from "react-router-dom";
import commonRoutes from "@me-team/common/src/router/Router";
import { GetCurrentBranchIdDocument, UpdateCurrentBranchDocument } from "../../main/branches/graphql/branches.hooks";

const mainUploadLink = createUploadLink({
    uri: `${process.env.REACT_APP_POS_URL}/graphql/`,
    credentials: "include",
});

const bookingUploadLink = createUploadLink({
    uri: `${process.env.REACT_APP_POS_URL}/graphql/booking`,
    credentials: "include",
});

const meBookingUploadLink = createUploadLink({
    uri: `${process.env.REACT_APP_POS_URL}/graphql/booking_restaurant`,
    credentials: "include",
});

const localeLink = setContext((_, { headers }) => {
    const locale = localStorage.getItem("i18nextLng") || "en";
    return {
        headers: {
            ...headers,
            "X-LOCALE": locale,
        },
    };
});

const checkAuthorization = async () => {
    try {
        const { data } = await mainClient.query({ query: MeUserDocument });
        const username = data?.me?.username ?? null;
        userUsernameVar(username);
        return username;
    } catch {
        userUsernameVar(null);
        return null;
    }
};

const checkBranch = async (categoryError: string, pathError: string): Promise<boolean> => {
    if ((categoryError === "user" || categoryError === "branch.not_found") && pathError === "updateCurrentBranch") {
        try {
            const currentBranchResponse = await mainClient.query({
                query: GetCurrentBranchIdDocument,
                fetchPolicy: "network-only",
            });

            const branchId = currentBranchResponse?.data?.user.currentBranch?.id;

            if (branchId) {
                window.location.href = `/branches/main/branch/${branchId}`;
            } else {
                window.location.href = `${process.env.REACT_APP_POS_URL}/login`;
            }

            return true;
        } catch (e) {
            console.error("Error while fetching current branch:", e);
            window.location.href = `${process.env.REACT_APP_POS_URL}/login`;
            return true;
        }
    }

    return false;
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const { errorType } = operation.getContext();

    if (errorType === "local") {
        if (graphQLErrors) {
            (async () => {
                for (const { path, extensions } of graphQLErrors) {
                    const handled = await checkBranch(extensions?.category as string, String(path));

                    if (handled) {
                        return;
                    }

                    if (extensions?.category === "user" || extensions?.category === "user.session_expired") {
                        const username = await checkAuthorization();

                        if (!username && window.location.pathname !== "/login") {
                            window.location.href = `${process.env.REACT_APP_POS_URL}/login`;
                        }
                    }
                }
            })();
        }

        if (networkError) {
            console.error(`[Network local error]: ${networkError}`);
        }

        return forward(operation);
    }

    if (errorType === "global" || errorType === undefined) {
        if (graphQLErrors) {
            (async () => {
                for (const { path, extensions } of graphQLErrors) {
                    const handled = await checkBranch(extensions?.category as string, String(path));

                    if (handled) {
                        return;
                    }

                    const username = await checkAuthorization();

                    if (
                        extensions?.category === "user" ||
                        extensions?.category === "user.session_expired" ||
                        extensions?.category === "user.access_denied_for_company" ||
                        extensions?.category === "user.no_active_companies"
                    ) {
                        if (!username && window.location.pathname !== "/login") {
                            window.location.href = `${process.env.REACT_APP_POS_URL}/login`;
                        }
                    } else if (
                        (extensions?.category === "internal" || path?.includes("internal")) &&
                        window.location.pathname !== "/error/500"
                    ) {
                        window.location.href = `/error/500?from=${encodeURIComponent(window.location.href)}`;
                    } else {
                        console.error(`[GraphQL error]: ${extensions?.code} at ${path}`);
                    }
                }
            })();
        }

        if (networkError) {
            console.error("Network Error:", networkError);

            if (window.location.pathname.includes("/create-owner")) {
                window.location.reload();
            } else if (window.location.pathname !== "/error/500") {
                window.location.href = `/error/500?from=${encodeURIComponent(window.location.href)}`;
            }
        }
    }
});

const mainClient = new ApolloClient({
    link: from([localeLink, errorLink, mainUploadLink]),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    me: {
                        read() {
                            return userUsernameVar();
                        },
                    },
                    userId: {
                        read() {
                            return userIdVar();
                        },
                    },
                    currentBranchId: {
                        read() {
                            return currentBranchIdVar();
                        },
                    },
                    currentCompanyId: {
                        read() {
                            return currentCompanyIdVar();
                        },
                    },
                },
            },
        },
    }),
});

const bookingClient = new ApolloClient({
    link: from([localeLink, errorLink, bookingUploadLink]),
    cache: new InMemoryCache(),
});

function getBranchIdFromLocation(loc: Location | string = window.location) {
    const matches = matchRoutes(commonRoutes, loc);
    const branchId = matches?.at(-1)?.params?.branchId as string | undefined;

    if (branchId && /^\d+$/.test(branchId)) {
        return branchId;
    }

    return undefined;
}

function findCompanyIdByBranchId(data: CompanyProxy[], targetBranchId: string): number | null {
    for (const company of data) {
        const branch = company.branches.find((b: BranchProxy) => b.id === Number(targetBranchId));
        if (branch) {
            return company.id;
        }
    }
    return null;
}

const isCreateOwnerRoute =
    window.location.pathname.startsWith("/create-owner") || window.location.pathname.startsWith("/registration");

const meBookingClient = new ApolloClient({
    link: from([localeLink, errorLink, meBookingUploadLink]),
    cache: new InMemoryCache(),
});

const refetchUserData = async () => {
    try {
        const branchIdFromUrl = getBranchIdFromLocation();
        let currentBranchId: number | null = null;

        if (branchIdFromUrl) {
            const parsed = Number(branchIdFromUrl);

            if (!Number.isNaN(parsed) && parsed.toString().length <= 9) {
                currentBranchId = parsed;
            }
        }

        const isErrorPage: boolean = window.location.pathname.includes("error");
        const isRegistrationPage: boolean = window.location.pathname.includes("registration");
        const isRestaurantBookingPage: boolean = window.location.pathname.includes("me-booking/restaurant-booking");

        if (currentBranchId === null && !isRegistrationPage) {
            const currentBranchResponse = await mainClient.query({
                query: GetCurrentBranchIdDocument,
                fetchPolicy: "network-only",
            });

            const backendBranchId = Number(currentBranchResponse?.data?.user?.currentBranch?.id);

            if (isRestaurantBookingPage) {
                currentBranchIdVar(backendBranchId);
                currentBranchId = backendBranchId;
            } else {
                if (Number.isNaN(backendBranchId)) {
                    console.error("Cannot resolve branch id from backend");
                    return;
                }

                if (isErrorPage) {
                    currentBranchId = backendBranchId;
                } else {
                    window.location.href = `/branches/main/branch/${backendBranchId}`;
                    return;
                }
            }
        }

        const isOnlyCreateOwnerRoute: boolean = window.location.pathname.startsWith("/create-owner");

        const userResponse = await mainClient.query({
            query: GetUserForGlobalVarDocument,
            fetchPolicy: "network-only",
            variables: {
                branchId: currentBranchId,
                userBranchId: isOnlyCreateOwnerRoute ? null : currentBranchId,
            },
        });
        const userId = userResponse?.data?.user?.id;
        const userCompaniesCount = userResponse?.data?.user?.companies?.length || 0;
        const currentCompanyId = userResponse?.data?.user?.currentBranch?.company?.id;
        const companyId =
            currentCompanyId || findCompanyIdByBranchId(userResponse?.data?.user.companies, branchIdFromUrl);
        const currentUserEmail = userResponse?.data?.user?.email;
        if (!isCreateOwnerRoute && (userCompaniesCount >= 1 || currentCompanyId)) {
            await mainClient.mutate({
                mutation: UpdateCurrentBranchDocument,
                variables: {
                    branchId: currentBranchId,
                    companyId: companyId,
                },
                fetchPolicy: "network-only",
            });
        }

        userIdVar(userId);
        currentBranchIdVar(currentBranchId);
        userCompaniesCountVar(userCompaniesCount);
        companyId && currentCompanyIdVar(companyId);
        currentUserEmailVar(currentUserEmail);

        if (companyId) {
            const companyResponse = await mainClient.query({
                query: GetCompanyGlobalDocument,
                variables: { companyId: +companyId, email: currentUserEmail, branchId: Number(currentBranchId) },
                fetchPolicy: "network-only",
            });
            const companyCurrencySign = companyResponse?.data?.user?.company?.currency?.currencySign;
            const userBranchRole =
                companyResponse?.data?.user?.company?.branches[0]?.employeesPaginated?.employees?.[0]?.userBranchRelations?.find(
                    (item: UserBranchRelationProxy) => item.branch?.id === currentBranchId
                )?.role;
            const companyBusinessType = companyResponse?.data?.user?.company?.category?.type;
            companyBusinessTypeVar(companyBusinessType);

            if (userBranchRole) {
                const accessServiceInstance = AccessService.getInstance();
                currencySingVar(companyCurrencySign);
                accessServiceInstance.setCompanyBusinessType(companyBusinessType);
            }
        }
    } catch (error) {
        console.error("Error fetching user or company data:", error);
    }
};

refetchUserData();

export const apolloClientMenu = mainClient;
export const refetchUserDataMenu = refetchUserData;
export const apolloClientBooking = bookingClient;
export const apolloClientMeBooking = meBookingClient;
